import "./Yearly.css"
import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import APIURL from "../../common/Config";
import Header from "../header/header";
import Footer from "../footer/footer";

const ScorpioCard = () => {
  const [horoscopeData, setHoroscopeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const inMemoryCacheRef = useRef(new Map());
  const [activeIndex, setActiveIndex] = useState(null);

  // Static metadata for SCORPIO
  const staticMeta = {
    title: "AstroBhagya – Free Scorpio Yearly Horoscope by Best Astrologer",
    description: "Looking for accurate Scorpio yearly horoscope predictions 2026? Discover free Scorpio astrology predictions and annual insights by zodiac sign for a better year ahead.",
    keywords: "Free Scorpio yearly horoscope predictions 2026, Scorpio yearly Astrology predictions, Scorpio annual predictions, Yearly horoscope by Scorpio zodiac sign",
    author: "Heavenly Expectation Private Limited",
  };

  const toggleAnswer = (index) => {
    setActiveIndex((prev) => (prev === index ? null : index));
  };

  const fetchScorpioYearly = useCallback(async (retries = 3) => {
    const dateStr = "20260101"; // 2026 data
    const cacheKey = `scorpio_yearly_${dateStr}`; // 🔥 SCORPIO cache key
    setError(null);
    setLoading(true);

    // 1. CHECK LOCALSTORAGE
    const cachedData = localStorage.getItem(cacheKey);
    if (cachedData) {
      try {
        const parsed = JSON.parse(cachedData);
        setHoroscopeData(parsed);
        setLoading(false);
        return;
      } catch (e) {
        console.error("Failed to parse cached data:", e);
      }
    }

    // 2. CHECK IN-MEMORY CACHE
    if (inMemoryCacheRef.current.has(cacheKey)) {
      setHoroscopeData(inMemoryCacheRef.current.get(cacheKey));
      setLoading(false);
      return;
    }

    const minLoadingTime = new Promise(resolve => setTimeout(resolve, 500));

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => {
        controller.abort();
        console.warn('Fetch request timed out after 15 seconds.');
      }, 15000);

      const response = await fetch(
        `${APIURL}/horroscope/getallprediction?type=yearly&startDate=${dateStr}&endDate=${dateStr}&language=english`,
        { signal: controller.signal }
      );

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (Array.isArray(data)) {
        // Find SCORPIO data (id: 37 or sunShine contains "SCORPIO") 
        const scorpioData = data.find(
          item => item.id === 37 || item.sunShine?.toUpperCase().includes("SCORPIO")
        );

        if (scorpioData) {
          // Cache the data
          inMemoryCacheRef.current.set(cacheKey, scorpioData);
          localStorage.setItem(cacheKey, JSON.stringify(scorpioData));
          setHoroscopeData(scorpioData);
        } else {
          throw new Error("Scorpio data not found in API response");
        }
      } else {
        throw new Error("Unexpected response format from API");
      }
    } catch (error) {
      if (error.name === 'AbortError') {
        setError('The network request timed out. Please check your internet connection and try again.');
        console.error("Network timeout error:", error);
      } else {
        setError(`Failed to load yearly horoscope: ${error.message}. Please try again.`);
        console.error("Error fetching Scorpio yearly data:", error);
      }

      if (retries > 0) {
        console.log(`Retrying fetch for Scorpio Yearly. Retries left: ${retries - 1}`);
        await new Promise(resolve => setTimeout(resolve, 3000));
        return fetchScorpioYearly(retries - 1);
      } else {
        // FALLBACK DATA - SCORPIO 2026 
        console.log("All retries exhausted. Using fallback data.");
        const fallbackData = {
          sunShine: "Scorpio 2026: Your year, as the stars align",
          prediction: `<div class="horoscope">
            <p>2026 brings Scorpio growth and renewal. Embrace change, step into power, and focus on long-term goals for transformation.</p>
            <h2>Love and Relationships</h2>
            <p>Relationships deepen through honesty and emotional connection. Trust and vulnerability strengthen bonds.</p>
            <h2>Career and Finances</h2>
            <p>Leadership opportunities reward strategic thinking. Disciplined financial planning ensures stability.</p>
            <h2>Health & Wellness</h2>
            <p>Balanced lifestyle prevents burnout. Yoga, meditation maintain emotional/physical health.</p>
            <h2>Astrological Remedies</h2>
            <ul>
              <li><strong>Black Onyx/Garnet:</strong> Protects emotional balance and enhances focus.</li>
              <li><strong>Spiritual Practice:</strong> Meditation processes emotional intensity.</li>
              <li><strong>Mars Remedies:</strong> Tuesday red flowers for energy/motivation.</li>
            </ul>
          </div>`
        };
        setHoroscopeData(fallbackData);
      }
    } finally {
      await minLoadingTime;
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchScorpioYearly();
  }, [fetchScorpioYearly]);

  // Set meta tags
  useEffect(() => {
    document.title = staticMeta.title;

    const setOrCreateMetaTag = (name, content) => {
      let metaTag = document.querySelector(`meta[name="${name}"]`);
      if (metaTag) {
        metaTag.setAttribute("content", content);
      } else {
        metaTag = document.createElement("meta");
        metaTag.setAttribute("name", name);
        metaTag.setAttribute("content", content);
        document.head.appendChild(metaTag);
      }
    };

    setOrCreateMetaTag("description", staticMeta.description);
    setOrCreateMetaTag("keywords", staticMeta.keywords);
    setOrCreateMetaTag("author", staticMeta.author);
    setOrCreateMetaTag("revisit-after", "7 days");
    setOrCreateMetaTag("distribution", "global");
    setOrCreateMetaTag("rating", "general");

    let charsetMeta = document.querySelector("meta[charset]");
    if (!charsetMeta) {
      charsetMeta = document.createElement("meta");
      charsetMeta.setAttribute("charset", "UTF-8");
      document.head.prepend(charsetMeta);
    }
  }, []);

  if (loading) {
    return (
      <div className="loader-container">
        <div className="spinner" />
        <p>Loading Scorpio 2026 Horoscope...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <p>{error}</p>
        <button className="retry-button" onClick={() => fetchScorpioYearly()}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div>
      <Header />
      <div>
        <section id="Make">
          <div className="year-container">
            <h4 className="mt-3 mb-3 text-center font-weight-bold">
              {horoscopeData?.sunShine || "Scorpio 2026: Your year, as the stars align"}
            </h4>
            <div className="icons-wrapper text-center mb-4">
              <img
                src={require("../../assets/Scorpiooo.webp")}
                alt="Scorpio"
                className="horoscope-icon mx-2"
              />
            </div>

            {horoscopeData?.prediction && (
              <main>
                <div
                  dangerouslySetInnerHTML={{
                    __html: horoscopeData.prediction
                  }}
                />
              </main>
            )}

            <div className="btn-wrapper">
              <Link to="/yearly-horoscope/libra" className="btn btn-customs">
                <i className="bi bi-arrow-left"></i> Libra Horoscope 2026
              </Link>
              <Link to="/yearly-horoscope/sagittarius" className="btn btn-customs">
                <i className="bi bi-arrow-right"></i> Sagittarius Horoscope 2026
              </Link>
            </div>
          </div>
        </section>

        {/* FAQ SECTION - SAME AS PREVIOUS */}
        <div className="faq-section">
          <div className="container-item">
            <h3>Frequently Asked Questions (FAQs) – Scorpio Yearly Horoscope</h3>
            <div className="fitem">
              {[
                {
                  question: "1. What are the core personality traits of Scorpio?",
                  answer: "Scorpio individuals are known for their intensity, determination, emotional depth, and transformative power. Ruled by Mars/Pluto, they excel in resilience and intuition.",
                },
                {
                  question: "2. What can Scorpio expect in love and relationships in 2026?",
                  answer: "2026 deepens relationships through honesty. Emotional vulnerability and trust create stronger, more meaningful bonds.",
                },
                {
                  question: "3. What does 2026 look like for Scorpio career growth?",
                  answer: "Leadership opportunities reward strategic thinking. Professional advancement through resilience and long-term planning.",
                },
                {
                  question: "4. What health advice for Scorpio in 2026?",
                  answer: "Balanced lifestyle prevents burnout. Yoga, meditation, and emotional self-care maintain vitality.",
                },
                {
                  question: "5. Key astrological remedies for Scorpio?",
                  answer: "Black Onyx/Garnet, meditation practice, and Tuesday Mars remedies enhance power and focus.",
                },
              ].map((item, index) => (
                <div className="faq-item" key={index}>
                  <div
                    className={`faq-question ${
                      activeIndex === index ? "active" : ""
                    }`}
                    onClick={() => toggleAnswer(index)}
                  >
                    {item.question}
                  </div>
                  <div
                    className={`faq-answer ${
                      activeIndex === index ? "show" : ""
                    }`}
                  >
                    {item.answer}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ScorpioCard; 
